<template lang="pug">

a-card.project-group(
  :id="projectGroup.name"
  :head-style="headStyle"
  :body-style="{ padding: 0, 'overflow-x': 'none' }"
  @click.stop="toggleFullViewMode"
)
  a-skeleton(
    v-if="loading"
    :paragraph="{rows: 1}"
    active
  )
  div(
    v-else
  )
    .project-group__description-wrapper(
      v-if="projectGroupsFullViewMode || fullViewMode"
    )
      read-more.project-group__description(
        v-if="!projectSearchText"
        :text="projectGroup.description"
        slot="description"
        :more-str="$t('components.project_group.tpl.view_more')"
        :less-str="$t('components.project_group.tpl.view_less')"
        :max-chars="descriptionMaxSize"
      )
      p.project-group__description(
        v-else
        v-html="description"
      )
    .project-group__keywords-wrapper(
      v-if="projectGroupsFullViewMode || fullViewMode"
    )
      project-group-keywords(
        :keywords="projectGroup.keywords"
      )
    project-group-slot-table(
      :project-group="projectGroup"
      :crd-project="crdProject"
    )
  template(
    slot="title"
  )
    .project-group__title
      a-skeleton(
        v-if="loading"
        active
      )
      a-row(
        v-else
        :gutter="10"
      )
        a-col.project-group__name(:xs="{span: showLogoAndColor ? 16 : 24}")
          b(
            v-if="projectSearchText"
            v-html="uppercaseName"
          )
          b(
            v-else
            v-html="projectGroup.name && projectGroup.name.toUpperCase()"
          )
        a-col.text-right(
          :xs="8"
          v-if="showLogoAndColor && logoSrc"
        )
          b-img.project-group__logo(
            :src="logoSrc"
            fluid
            alt="Responsive image"
          )
</template>

<script>
import {
  orderBy,
  toUpper,
  find,
  negate,
  overEvery,
} from 'lodash';

import { mapState } from 'vuex';

import {
  getColorsFromSrc,
  highlightWord,
  projectIsTest,
} from '@/util';

import ProjectGroupKeywords from '@/components/ProjectGroupKeywords';
import ProjectGroupSlotTable from '@/components/ProjectGroupSlotTable';

export const DEFAULT_DESCRIPTION = '';
export const DEFAULT_NAME = '';
export const DEFAULT_KEYWORDS = null;

export const DESCRIPTION_MAX_SIZE = 250;

export default {
  components: {
    ProjectGroupKeywords,
    ProjectGroupSlotTable,
  },
  props: {
    projectGroup: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      uppercaseName: '',
      description: '',
      descriptionMaxSize: DESCRIPTION_MAX_SIZE,
      logoSrc: '',
      logoColors: {},
      keywords: '',
      loading: false,
      fullViewMode: false,
      showLogoAndColor: true,
    };
  },
  computed: {
    ...mapState([
      'projectSearchText',
      'projectGroupsFullViewMode',
    ]),
    ...mapState('login', [
      'instance',
    ]),
    orderedProjects() {
      return orderBy(this.projectGroup.projects, 'id');
    },
    crdProject() {
      const projectTypeIsCrd = project => project.type === 'CRD';
      const projectIsNotTest = negate(projectIsTest);
      const projectTypeIsCrdAndIsNotTest = overEvery(projectTypeIsCrd, projectIsNotTest);
      const crdProjectNotTest = find(this.orderedProjects, projectTypeIsCrdAndIsNotTest);

      return crdProjectNotTest;
    },
    colors() {
      const DEFAULT_COLORS = {
        dominant: 'white',
        secondary: 'white',
        palette: [],
      };

      if (this.logoColors) {
        let { dominant } = this.logoColors;

        if (dominant === 'rgb(255,255,255)') {
          dominant = 'white';
        }

        return {
          dominant,
          secondary: this.logoColors.secondary,
          palette: this.logoColors.secondary,
        };
      }

      return DEFAULT_COLORS;
    },
    headStyle() {
      return {
        backgroundColor: this.colors && this.colors.dominant && this.colors.dominant.replace(')', ', 0.1)').replace('rgb', 'rgba'),
      };
    },
  },
  watch: {
    projectSearchText(val) {
      if (val) {
        this.highlight();
      }
    },
    projectGroupsFullViewMode(val) {
      this.fullViewMode = val;
    },
  },
  async beforeMount() {
    if (this.projectSearchText) {
      this.highlight();
    }

    this.loading = true;
    await this.setLogoSrc();

    this.logoColors = this.logoSrc && await getColorsFromSrc(this.logoSrc);
    this.loading = false;
  },
  methods: {
    setUppercaseName() {
      this.uppercaseName = this.name && toUpper(this.name);
    },
    async setLogoSrc() {
      if (this.instance === 'gecp') {
        return;
      }

      this.logoSrc = this.projectGroup.headerLeft;

      if (!this.logoSrc) {
        return;
      }

      // remove domain from src tag
      this.logoSrc = this.logoSrc.substring(
        this.logoSrc.indexOf('/repository'),
        this.logoSrc.length,
      );
    },
    highlight() {
      this.description = highlightWord(
        this.projectGroup.description || '',
        this.projectSearchText,
      );
      this.uppercaseName = toUpper(highlightWord(
        this.projectGroup.name || '',
        this.projectSearchText,
      ));
    },
    toggleFullViewMode(event) {
      if (event.target.tagName === 'BUTTON'
        || event.target.tagName === 'A'
        || !this.projectGroup.description) {
        return;
      }

      this.fullViewMode = !this.fullViewMode;
    },
  },
};
</script>

<style>
.box-shadow {
  box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.05);
}
.project-group {
  font-family: "Montserrat", sans-serif;
}
.project-group__title {
  height: 35px;
}
.project-group__name {
  font-weight: bold;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
.project-group__logo {
  height: 35px;
}
.project-group__keywords-wrapper {
  padding: 15px;
}
.project-group__description-wrapper {
  padding: 15px;
  min-height: 150px;
  min-height: 150px;
  max-height: 150px;
  overflow-y: auto;
}
.project-group__description * {
  font-size: 0.9rem !important;
  color: #868e96 !important;
  text-align: left !important;
}
#readmore {
  color: #007bff !important;
}
</style>

